import { FC, ReactNode } from 'react'
import { Box, Chip, Link, Tooltip, Typography } from '@mui/material'
import format from 'date-fns/format'
import { GridColDef } from '@mui/x-data-grid-premium'
import { SensorConfigurationRequest } from 'src/api'
import { getConfigurationRequestAuthorMeta, getConfigurationRequestErrorSummary, getConfigurationRequestStatusMeta } from './sensorConfigurationRequestHelpers'

export interface SensorConfigurationRequestPayload {
  title: string
  value: object | null
}

interface StatusChipProps {
  status?: string | null
}

/** Sent/error badge, shared by the configuration history and the configuration analytics grid. */
export const SensorConfigurationRequestStatusChip: FC<StatusChipProps> = ({ status }) => {
  if (!status) return null

  const { label, color } = getConfigurationRequestStatusMeta(status)

  return <Chip label={label} color={color} size="small" />
}

interface ViewPayloadLinkProps {
  onClick: () => void
  children: ReactNode
}

const ViewPayloadLink: FC<ViewPayloadLinkProps> = ({ onClick, children }) => (
  <Link type="button" component="button" onClick={onClick} sx={{ fontSize: 'inherit', whiteSpace: 'nowrap' }}>
    <Typography variant="body2">{children}</Typography>
  </Link>
)

type OnViewPayload = (payload: SensorConfigurationRequestPayload) => void

export const getSensorConfigurationRequestColumns = (onViewPayload: OnViewPayload): GridColDef<SensorConfigurationRequest>[] => [
  {
    field: 'createdAt',
    headerName: 'Datetime',
    type: 'string',
    minWidth: 200,
    flex: 1,
    sortable: false,
    disableColumnMenu: true,
    valueFormatter: ({ value }) => (value ? format(new Date(value as string), 'PPpp') : '-')
  },
  {
    field: 'status',
    headerName: 'Request status',
    type: 'string',
    minWidth: 160,
    flex: 1,
    sortable: false,
    disableColumnMenu: true,
    valueGetter: ({ row }) => getConfigurationRequestStatusMeta(row.status).label,
    renderCell: ({ row }) => <SensorConfigurationRequestStatusChip status={row.status} />
  },
  {
    field: 'errorDetails',
    headerName: 'Error details',
    type: 'string',
    minWidth: 240,
    flex: 2,
    sortable: false,
    disableColumnMenu: true,
    valueGetter: ({ row }) => getConfigurationRequestErrorSummary(row.errorDetails),
    renderCell: ({ row, value }) => {
      if (!row.errorDetails) return ''

      return (
        <Box display="flex" alignItems="center" gap={1} minWidth={0} overflow="hidden">
          <Tooltip title={value as string}>
            <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
              {value as string}
            </Typography>
          </Tooltip>
          <ViewPayloadLink onClick={() => onViewPayload({ title: `Error details of request #${row.id}`, value: row.errorDetails || null })}>view</ViewPayloadLink>
        </Box>
      )
    }
  },
  {
    field: 'configuration',
    headerName: 'Configuration',
    type: 'string',
    minWidth: 140,
    flex: 1,
    sortable: false,
    disableColumnMenu: true,
    valueGetter: ({ row }) => (row.configuration ? JSON.stringify(row.configuration) : ''),
    renderCell: ({ row }) => {
      if (!row.configuration || Object.keys(row.configuration).length === 0) return '-'

      return (
        <ViewPayloadLink onClick={() => onViewPayload({ title: `Configuration sent in request #${row.id}`, value: row.configuration || null })}>view configuration</ViewPayloadLink>
      )
    }
  },
  {
    field: 'author',
    headerName: 'Author',
    type: 'string',
    minWidth: 220,
    flex: 1.5,
    sortable: false,
    disableColumnMenu: true,
    valueGetter: ({ row }) => getConfigurationRequestAuthorMeta(row).label,
    renderCell: ({ row, value }) => {
      const { sourceLabel, source } = getConfigurationRequestAuthorMeta(row)

      return (
        <Box minWidth={0}>
          <Typography variant="body2" noWrap lineHeight={1.3}>
            {value as string}
          </Typography>
          <Tooltip title={source ? `Source: ${source}` : ''}>
            <Typography variant="caption" color="text.secondary" noWrap component="div" lineHeight={1.3}>
              {sourceLabel}
            </Typography>
          </Tooltip>
        </Box>
      )
    }
  }
]
